import { ReactElement } from 'react';
import { useLocale } from '@hooks/useLocale';
import {
  BrandedStoreSliceType,
  BrandedStoreSlice,
} from '@adapters/prismic/types/brandedStorePage';
import Hero from '../BrandedStoreSlices/Hero';
import ImageComponent from '../BrandedStoreSlices/ImageComponent';
import PhotoGrid from '../BrandedStoreSlices/PhotoGrid';
import BrandValues from '../BrandedStoreSlices/BrandValues';
import CollectionAssortment from '../BrandedStoreSlices/CollectionAssortment';
import HeroPLP from '../BrandedStoreSlices/HeroPLP';
import FeaturedBox from '../BrandedStoreSlices/FeaturedBox';
import ProductGrid from '../ProductGrid';
import FeaturedBestSellers from '../BrandedStoreSlices/FeaturedBestSellers';
import CategoryNavCarousel from '../Slices/CategoryNavCarousel';
import { PageType } from '@adapters/prismic/types/slices';
import BestSellerRecommendations from '../BrandedStoreSlices/BestSellerRecommendations';
import { AlgonomyPlacement } from '@components/global/Recommendations/Loader';

const BrandedStoreSliceHandler = ({
  slice,
  pageType,
  sliceIndex,
  recommendations,
  brandedStoreCoopAttribute,
}: {
  slice: BrandedStoreSlice;
  pageType: PageType;
  sliceIndex: number;
  recommendations?: AlgonomyPlacement[];
  brandedStoreCoopAttribute?: string | null;
}): ReactElement | null => {
  const locale = useLocale();
  const collectionAssortmentRecommendations1 = recommendations?.filter(
    (recommendation) => recommendation.name === 'brand_page.brandcollection1_rr'
  );

  const collectionAssortmentRecommendations2 = recommendations?.filter(
    (recommendation) => recommendation.name === 'brand_page.brandcollection2_rr'
  );

  const bestSellersRecommendations = recommendations?.filter(
    (recommendation) => recommendation.name === 'brand_page.brandbestsellers_rr'
  );

  switch (slice?.slice_type) {
    case BrandedStoreSliceType.hero:
      return (
        <Hero
          locale={locale}
          primary={slice?.primary}
          sliceIndex={sliceIndex}
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
        />
      );
    case BrandedStoreSliceType.collectionAssortment:
      return (
        <CollectionAssortment
          locale={locale}
          primary={slice?.primary}
          items={slice?.items}
          products={
            slice?.primary?.collection_path === 'brand_page.brandcollection1_rr'
              ? collectionAssortmentRecommendations1?.[0]?.items
              : collectionAssortmentRecommendations2?.[0]?.items
          }
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.photoGrid:
      return (
        <PhotoGrid
          locale={locale}
          items={slice?.items}
          primary={slice?.primary}
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.categoryNavCarousel:
      return (
        <CategoryNavCarousel
          title={slice.primary.title}
          items={slice.items}
          pageType={pageType}
          sliceIndex={sliceIndex}
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
          trackPromos
        />
      );
    case BrandedStoreSliceType.brandValues:
      return (
        <BrandValues
          primary={slice?.primary}
          items={slice?.items}
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.heroPLP:
      return <HeroPLP primary={slice?.primary} />;
    case BrandedStoreSliceType.featuredBox:
      return (
        <FeaturedBox
          primary={slice?.primary}
          locale={locale}
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.productGridWithTooltips:
      return (
        <ProductGrid
          title={slice.primary.title}
          id={slice?.primary?.id}
          body={slice?.primary?.copy?.[0]?.text}
          images={slice?.items}
          cta={{
            text: slice.primary.cta_text,
            url: slice.primary.cta_url,
            importance: slice.primary.cta_importance,
          }}
          altPosition={slice.primary?.alt_position}
          icon={slice.primary?.icon}
          socialProof={{
            top: slice?.primary?.social_proof_top?.[0]?.text ?? '',
            bottom: slice?.primary?.social_proof_bottom?.[0]?.text ?? '',
            topSize: slice.primary.social_proof_top_size,
            bottomSize: slice.primary.social_proof_bottom_size,
          }}
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.featuredBestSellers:
      return (
        <FeaturedBestSellers
          primary={slice?.primary}
          locale={locale}
          products={bestSellersRecommendations?.[0]?.items}
        />
      );
    case BrandedStoreSliceType.imageComponent:
      return (
        <ImageComponent
          primary={slice?.primary}
          items={slice?.items}
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.bestSellerRecommendations:
      return (
        <BestSellerRecommendations
          featuredBestSellersRecommendations={bestSellersRecommendations}
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
          sliceIndex={sliceIndex}
        />
      );
    default:
      return null;
  }
};

export default BrandedStoreSliceHandler;
